import type {
  DataVizChart,
  DataVizChartKind,
  DataVizChartPoint,
  DataVizChartSeries,
} from "@triedge/data-viz-three";
import type {
  ReportGenerationAdvancedVisualizationChartOption,
  ReportGenerationAdvancedVisualizationChartPoint,
} from "./types";

const THREE_CHART_TYPES = new Set<string>(["bar", "line"]);
const THREE_RENDER_KINDS = new Set<string>(["bar", "line", "grouped-bar", "ribbon-line"]);
const DEFAULT_SERIES_COLORS = [
  "#2697A2",
  "#237062",
  "#08BFAE",
  "#C57A00",
  "#7C3AED",
  "#CC4444",
];

const normalizeChartIdPart = (value: string | null | undefined): string =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._:-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "unknown";

const finiteChartValue = (
  point: ReportGenerationAdvancedVisualizationChartPoint,
): number | null => {
  if (typeof point.value === "number" && Number.isFinite(point.value)) {
    return point.value;
  }
  if (typeof point.sum === "number" && Number.isFinite(point.sum)) {
    return point.sum;
  }
  if (typeof point.average === "number" && Number.isFinite(point.average)) {
    return point.average;
  }
  if (typeof point.count === "number" && Number.isFinite(point.count)) {
    return point.count;
  }
  return null;
};

export const isReportGenerationThreeChartType = (
  chartType: string | null | undefined,
): chartType is DataVizChartKind => THREE_CHART_TYPES.has(String(chartType ?? ""));

export const projectReportGenerationAdvancedVisualizationChart = (
  option: ReportGenerationAdvancedVisualizationChartOption,
  points: readonly ReportGenerationAdvancedVisualizationChartPoint[],
): DataVizChart | null => {
  if (!isReportGenerationThreeChartType(option.chartType)) {
    return null;
  }
  const renderKind = THREE_RENDER_KINDS.has(String(option.renderKind ?? ""))
    ? option.renderKind as DataVizChartKind
    : option.chartType as DataVizChartKind;
  const projectedEntries = points.flatMap((point, index) => {
    const value = finiteChartValue(point);
    if (value == null || value < 0) {
      return [];
    }
    const seriesId = normalizeChartIdPart(point.seriesId ?? option.metricFieldId ?? option.id);
    const seriesLabel = point.seriesLabel ?? option.metricLabel ?? option.label;
    const projectedPoint: DataVizChartPoint = {
      id: `${normalizeChartIdPart(option.id)}:${seriesId}:${index}`,
      key: normalizeChartIdPart(point.key || point.bucket || point.label || String(index)),
      label: point.label || point.bucket || `Point ${index + 1}`,
      value,
      count: Number.isFinite(point.count) ? point.count : undefined,
      bucket: point.bucket ?? point.label ?? null,
      metadata: {
        aggregate: option.aggregate,
        source: "preview-aggregate",
        seriesId,
        seriesLabel,
      },
    };
    return [{
      point: projectedPoint,
      seriesId,
      seriesLabel,
      seriesColor: point.seriesColor,
    }];
  });

  if (projectedEntries.length === 0) {
    return null;
  }

  const valueTotal = projectedEntries.reduce((total, entry) => total + entry.point.value, 0);
  const maxValue = Math.max(...projectedEntries.map((entry) => entry.point.value), 1);
  const seriesById = new Map<string, DataVizChartSeries>();
  projectedEntries.forEach((entry) => {
    const existing = seriesById.get(entry.seriesId);
    if (existing) {
      existing.points.push({
        ...entry.point,
        normalizedValue: entry.point.value / maxValue,
      });
      return;
    }
    seriesById.set(entry.seriesId, {
      id: entry.seriesId,
      label: entry.seriesLabel,
      color: entry.seriesColor ?? DEFAULT_SERIES_COLORS[seriesById.size % DEFAULT_SERIES_COLORS.length],
      points: [{
        ...entry.point,
        normalizedValue: entry.point.value / maxValue,
      }],
      metadata: {
        aggregate: option.aggregate,
        source: "preview-aggregate",
      },
    });
  });

  return {
    kind: renderKind,
    title: option.label,
    axes: {
      x: {
        label: option.dimensionLabel ?? option.xLabel ?? "Bucket",
      },
      y: {
        label: option.metricLabel ?? option.yLabel ?? "Value",
        min: 0,
        max: maxValue,
      },
    },
    series: [...seriesById.values()],
    metadata: {
      actualData: true,
      animationVariant: option.animationVariant ?? "default",
      chartSource: "preview-aggregate",
      optionId: option.id,
      renderKind,
      useCaseId: option.useCaseId ?? option.id,
      pointCount: projectedEntries.length,
      seriesCount: seriesById.size,
      valueTotal,
    },
  };
};
